import React, { useEffect, useState, useRef } from 'react';
import GameInstancesFilterPanel from './GameInstancesFilterPanel.js';
import AddressDisplay, { AddressDisplayLong } from './AddressDisplay.js';
import { useGameInstances } from '../hooks/useBlockchainData.js';
import useDrawOffers from '../hooks/useDrawOffers';
import { useBlockchainState } from '../contexts/BlockchainStateContext.js';

import './sidebars.css';

const GameInstanceCard = ({
  gameInstance,
  killGame,
  offerDraw,
  acceptDrawOffer,
  withdrawDrawOffer,
  drawOffers,
  playerAccount,
  gameUrls,
  backgroundColor,
  ethersUtils,
  games }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDrawOfferForm, setShowDrawOfferForm] = useState(false);
  const [drawPayout1, setDrawPayout1] = useState('');
  const [drawPayout2, setDrawPayout2] = useState('');

  // Find the game address from the games array using the game name
  const getGameAddress = () => {
    if (!games || !gameInstance.gameName) return gameInstance.gameAddress || null;
    const game = games.find(g => g.name === gameInstance.gameName);
    return game ? game.gameAddress : (gameInstance.gameAddress || null);
  };

  const gameAddress = getGameAddress();

  const isPlayer1 = gameInstance.player1 && String(gameInstance.player1).toLowerCase() === (playerAccount || '').toLowerCase();
  const isPlayer2 = gameInstance.player2 && String(gameInstance.player2).toLowerCase() === (playerAccount || '').toLowerCase();

  const lastMoveTime = new Date(Number(gameInstance.lastMoveTime) * 1000);
  const timeElapsedSinceLastMove = Math.floor((Date.now() - lastMoveTime.getTime()) / (1000 * 60)); // in minutes
  const timeoutMinutes = Number(gameInstance.timeoutSinceLastMove) / 60;
  
  // Handle time sync issues - if elapsed time is negative, assume timeout expired
  const timeElapsedCorrected = Math.max(0, timeElapsedSinceLastMove);
  const timeRemaining = Math.max(0, timeoutMinutes - timeElapsedCorrected);
  
  // More robust killswitch detection - handle time sync issues
  const isTimeoutExpired = (timeElapsedSinceLastMove > timeoutMinutes) || (timeElapsedSinceLastMove < 0 && Math.abs(timeElapsedSinceLastMove) > timeoutMinutes);

  // Enhanced killswitch authorization - check game state and player authorization
  const canPlayerKillGame = () => {
    if (!isTimeoutExpired) return false;

    const status = ethersUtils.toBN(gameInstance.status || 0);

    // Player1turn (0) → Only Player2 can kill (Player1 is at fault)
    if (status.eq(ethersUtils.toBN(0))) return isPlayer2;

    // Player2turn (1) → Only Player1 can kill (Player2 is at fault)
    if (status.eq(ethersUtils.toBN(1))) return isPlayer1;

    // BothPlayerturn (6) → Either player can kill (both at fault)
    if (status.eq(ethersUtils.toBN(6))) return isPlayer1 || isPlayer2;

    // Game finished or invalid state → No one can kill
    return false;
  };

  const isKillSwitchEnabled = canPlayerKillGame();
  const killButtonClass = isKillSwitchEnabled ? '' : 'grey';

  // Determine if it's waiting for the current player
  const normalizedStatus = ethersUtils.toBN(gameInstance.status || 0);
  const UserIsP1AndTurn = (isPlayer1 && (normalizedStatus.eq(ethersUtils.toBN(0)) || normalizedStatus.eq(ethersUtils.toBN(6))));
  const UserIsP2AndTurn = (isPlayer2 && (normalizedStatus.eq(ethersUtils.toBN(1)) || normalizedStatus.eq(ethersUtils.toBN(6))));
  const isWaitingForMe = UserIsP1AndTurn || UserIsP2AndTurn;

  const joinButtonClass = 'blue';

  const handleCardClick = (e) => {
    // Only prevent default on left clicks to allow right-click context menu
    if (e.button === 0 || e.type === 'click') {
      e.stopPropagation(); // Prevent the event from bubbling up to the outer collapsible box
      setIsExpanded(!isExpanded);
    }
  };

  // Draw offer handlers
  const handleOfferDraw = async (e) => {
    e.stopPropagation();
    if (!drawPayout1 || !drawPayout2) {
      alert('Please enter both payout amounts');
      return;
    }
    await offerDraw(gameInstance.offerID, parseFloat(drawPayout1), parseFloat(drawPayout2));
    setShowDrawOfferForm(false);
    setDrawPayout1('');
    setDrawPayout2('');
  };

  const handleAcceptDraw = async (e, otherPlayerAddress) => {
    e.stopPropagation();
    await acceptDrawOffer(gameInstance.offerID, otherPlayerAddress);
  };

  const handleWithdrawDraw = async (e) => {
    e.stopPropagation();
    await withdrawDrawOffer(gameInstance.offerID);
  };

  // Calculate default payouts (equal split)
  const totalPot = parseFloat(ethersUtils.fromWei(gameInstance.player1Bet.toString(), 'ether')) + 
                   parseFloat(ethersUtils.fromWei(gameInstance.player2Bet.toString(), 'ether'));
  const defaultPayout = (totalPot / 2).toFixed(4);

  // Calculate player outcome for completed games
  const getPlayerOutcome = () => {
    const status = normalizedStatus;
    const player1Bet = parseFloat(ethersUtils.fromWei(gameInstance.player1Bet.toString(), 'ether'));
    const player2Bet = parseFloat(ethersUtils.fromWei(gameInstance.player2Bet.toString(), 'ether'));
    
    
    // Only for completed games
    if (!status.eq(ethersUtils.toBN(2)) && !status.eq(ethersUtils.toBN(3)) && !status.eq(ethersUtils.toBN(4)) && !status.eq(ethersUtils.toBN(5))) {
      return { type: 'active', amount: totalPot, color: 'bg-white' }; // Active games - show total bet
    }
    
    if (status.eq(ethersUtils.toBN(3))) { // Player 1 won
      if (isPlayer1) {
        return { type: 'won', amount: totalPot - player1Bet, color: 'bg-green-100' }; // Won amount (total pot - own bet)
      } else if (isPlayer2) {
        return { type: 'lost', amount: -player2Bet, color: 'bg-red-100' }; // Lost own bet
      }
    } else if (status.eq(ethersUtils.toBN(4))) { // Player 2 won
      if (isPlayer2) {
        return { type: 'won', amount: totalPot - player2Bet, color: 'bg-green-100' }; // Won amount (total pot - own bet)
      } else if (isPlayer1) {
        return { type: 'lost', amount: -player1Bet, color: 'bg-red-100' }; // Lost own bet
      }
    } else if (status.eq(ethersUtils.toBN(2))) { // Draw
      if (isPlayer1 || isPlayer2) {
        return { type: 'draw', amount: 0, color: 'bg-yellow-100' }; // Draw - no win/loss
      }
    } else if (status.eq(ethersUtils.toBN(5))) { // Killed
      if (isPlayer1 || isPlayer2) {
        return { type: 'killed', amount: 0, color: 'bg-red-100' }; // Killed game
      }
    }
    
    // Default for viewers or unknown states
    return { type: 'unknown', amount: totalPot, color: 'bg-white' };
  };

  const playerOutcome = getPlayerOutcome();

  // Map outcome types to actual background colors
  const getBackgroundColor = (type) => {
    switch (type) {
      case 'won': return '#dcfce7'; // light green (bg-green-100)
      case 'lost': return '#fee2e2'; // light red (bg-red-100)  
      case 'draw': return '#fef3c7'; // light yellow (bg-yellow-100)
      case 'killed': return '#fee2e2'; // light red (bg-red-100)
      default: return '#ffffff'; // white (bg-white)
    }
  };

  // Auto-calculate remaining amount handlers
  const handlePayout1Blur = () => {
    if (drawPayout1 && !isNaN(parseFloat(drawPayout1))) {
      const payout1 = parseFloat(drawPayout1);
      const remaining = Math.max(0, totalPot - payout1);
      setDrawPayout2(remaining.toFixed(4));
    }
  };

  const handlePayout2Blur = () => {
    if (drawPayout2 && !isNaN(parseFloat(drawPayout2))) {
      const payout2 = parseFloat(drawPayout2);
      const remaining = Math.max(0, totalPot - payout2);
      setDrawPayout1(remaining.toFixed(4));
    }
  };

  // Initialize default payouts when opening form
  const openDrawOfferForm = (e) => {
    e.stopPropagation();
    if (!drawPayout1 && !drawPayout2) {
      setDrawPayout1(defaultPayout);
      setDrawPayout2(defaultPayout);
    }
    setShowDrawOfferForm(true);
  };

  // Build the game URL for proper linking
  const getGameUrl = () => {
    const baseUrl = gameUrls[gameInstance.gameName];
    if (baseUrl && baseUrl.includes('?')) {
      // Handle URLs with query parameters (like Risk games)
      const [path, params] = baseUrl.split('?');
      return `${path}/${gameInstance.gameID}?${params}`;
    } else {
      // Handle simple URLs
      return `${baseUrl}/${gameInstance.gameID}`;
    }
  };

  return (
    <div 
      className="border rounded-lg shadow-md mb-4"
      style={{ backgroundColor: playerOutcome.type === 'active' ? backgroundColor : getBackgroundColor(playerOutcome.type) }}
      onContextMenu={(e) => e.stopPropagation()} // Allow right-click context menu
      onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up to parent collapsible
    >
      {/* Always visible header - only the expand/collapse indicator is clickable */}
      <div className="p-4">
        <div className="flex justify-between">
          <div className="text-lg font-bold">{gameInstance.gameName}</div>
          <div className="text-sm">
            Timeout: {timeoutMinutes} min 
            <span className="ml-2 text-gray-600">
              (remaining: {timeRemaining.toFixed(0)} min)
            </span>
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <div className="text-sm">
            {playerOutcome.type === 'active' ? (
              `Bet: ${totalPot.toFixed(4)} POL`
            ) : (
              playerOutcome.amount >= 0 ? `Won: ${playerOutcome.amount.toFixed(4)} POL` : `Lost: ${Math.abs(playerOutcome.amount).toFixed(4)} POL`
            )}
            {isWaitingForMe && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">waiting for me</span>}
          </div>
          <div className="text-sm">Status: {gameInstance.status_string}</div>
        </div>
        
        {/* Expand/Collapse indicator - ONLY clickable area */}
        <div 
          className="text-center mt-2 text-gray-500 text-sm cursor-pointer hover:bg-gray-200 py-2 rounded transition"
          onClick={handleCardClick}
        >
          {isExpanded ? '▲ Click to collapse' : '▼ Click to expand'}
        </div>
      </div>

      {/* Expanded content area - no click handler, so no interference with interactions */}
      {isExpanded && (
        <div 
          className="px-4 pb-4 space-y-2 border-t border-gray-200"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div className="pt-4">
            <div className="text-sm">Game ID: {gameInstance.gameID.toString()}</div>
            <div className="text-sm">Offer ID: {gameInstance.offerID.toString()}</div>
            <div className="text-sm">Game Address: <AddressDisplayLong address={gameAddress} useExplorerLink={true} /></div>
            <div className="text-sm">Player 1: <AddressDisplayLong address={gameInstance.player1} useExplorerLink={true} /> (Bet: {ethersUtils.fromWei(gameInstance.player1Bet.toString(), 'ether')})</div>
            <div className="text-sm">Player 2: <AddressDisplayLong address={gameInstance.player2} useExplorerLink={true} /> (Bet: {ethersUtils.fromWei(gameInstance.player2Bet.toString(), 'ether')})</div>

            <div className="flex items-center space-x-4 mt-4">
              <a 
                href={getGameUrl()}
                className={`inline-block bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-center transition ${joinButtonClass}`}
              >
                Join
              </a>
              {(isPlayer1 || isPlayer2) && gameInstance.gameActive && (
                <button 
                  onClick={() => killGame(gameInstance.offerID)}
                  className={`bg-red-500 text-white px-4 py-2 rounded-md ${killButtonClass}`}
                  disabled={!isKillSwitchEnabled}
                >
                  KillSwitch
                </button>
              )}
              {(isPlayer1 || isPlayer2) && gameInstance.gameActive && (
                <button 
                  onClick={openDrawOfferForm}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md transition"
                >
                  Offer Draw
                </button>
              )}
            </div>
          </div>

          {/* Draw Offer Form */}
          {showDrawOfferForm && (
            <div 
              className="mt-4 p-4 bg-yellow-50 rounded-md border border-yellow-200"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <h4 className="font-semibold mb-2">Propose Draw with Custom Payouts</h4>
              <div className="text-sm text-gray-600 mb-3">
                Total pot: {totalPot.toFixed(4)} POL
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player 1 Payout (ETH):
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    max={totalPot}
                    value={drawPayout1}
                    onChange={(e) => setDrawPayout1(e.target.value)}
                    onBlur={handlePayout1Blur}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player 2 Payout (ETH):
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    max={totalPot}
                    value={drawPayout2}
                    onChange={(e) => setDrawPayout2(e.target.value)}
                    onBlur={handlePayout2Blur}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.0000"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={handleOfferDraw}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm"
                  disabled={!drawPayout1 || !drawPayout2 || (parseFloat(drawPayout1) + parseFloat(drawPayout2)) > totalPot}
                >
                  Submit Draw Offer
                </button>
                <button 
                  onClick={() => setShowDrawOfferForm(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => { 
                    setDrawPayout1(defaultPayout); 
                    setDrawPayout2(defaultPayout); 
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Equal Split
                </button>
              </div>
            </div>
          )}

          {/* Draw Offers Status */}
          {(isPlayer1 || isPlayer2) && gameInstance.gameActive && (
            <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
              <h5 className="font-medium text-blue-800 mb-2">Draw Offers</h5>
              <div className="text-sm space-y-2">
                
                {/* Player 1 Draw Offer */}
                {drawOffers.player1Offer && drawOffers.player1Offer.active ? (
                  <div className={`p-2 rounded ${drawOffers.player1Stale ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="font-medium text-gray-700">
                      Player 1's Draw Offer {drawOffers.player1Stale ? '(Expired)' : ''}
                    </div>
                    <div className="text-xs text-gray-600">
                      Player 1: {ethersUtils.fromWei(drawOffers.player1Offer.proposedPayout1.toString(), 'ether')} POL
                      {' | '}
                      Player 2: {ethersUtils.fromWei(drawOffers.player1Offer.proposedPayout2.toString(), 'ether')} POL
                    </div>
                    {!drawOffers.player1Stale && isPlayer2 && (
                      <div className="mt-1 space-x-2">
                        <button 
                          onClick={(e) => handleAcceptDraw(e, gameInstance.player1)}
                          className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Accept
                        </button>
                      </div>
                    )}
                    {isPlayer1 && !drawOffers.player1Stale && (
                      <div className="mt-1">
                        <button 
                          onClick={handleWithdrawDraw}
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Withdraw
                        </button>
                      </div>
                    )}
                  </div>
                ) : isPlayer1 && (
                  <div className="text-gray-500 text-xs">No active draw offer from you</div>
                )}

                {/* Player 2 Draw Offer */}
                {drawOffers.player2Offer && drawOffers.player2Offer.active ? (
                  <div className={`p-2 rounded ${drawOffers.player2Stale ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="font-medium text-gray-700">
                      Player 2's Draw Offer {drawOffers.player2Stale ? '(Expired)' : ''}
                    </div>
                    <div className="text-xs text-gray-600">
                      Player 1: {ethersUtils.fromWei(drawOffers.player2Offer.proposedPayout1.toString(), 'ether')} POL
                      {' | '}
                      Player 2: {ethersUtils.fromWei(drawOffers.player2Offer.proposedPayout2.toString(), 'ether')} POL
                    </div>
                    {!drawOffers.player2Stale && isPlayer1 && (
                      <div className="mt-1 space-x-2">
                        <button 
                          onClick={(e) => handleAcceptDraw(e, gameInstance.player2)}
                          className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Accept
                        </button>
                      </div>
                    )}
                    {isPlayer2 && !drawOffers.player2Stale && (
                      <div className="mt-1">
                        <button 
                          onClick={handleWithdrawDraw}
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Withdraw
                        </button>
                      </div>
                    )}
                  </div>
                ) : isPlayer2 && (
                  <div className="text-gray-500 text-xs">No active draw offer from you</div>
                )}

                {/* Info message */}
                <div className="text-xs text-gray-500 mt-2">
                  Draw offers automatically expire when a player makes a move
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const GameInstancesTable = ({
  playerAccount,
  killGame,
  offerDraw,
  acceptDrawOffer,
  withdrawDrawOffer,
  gameUrls,
  games,
  ethersUtils,
  hideWrapper = false, // New prop to hide the collapsible wrapper
  preFilteredGameInstances = null // Pre-filtered game instances from PersonalizedSection
}) => {
  const tableRef = useRef(null);

  // DICY-style initialization guards to prevent re-render loops
  const [isInitialized, setIsInitialized] = useState(false);
  const preFilteredRef = useRef(preFilteredGameInstances);
  const logCounterRef = useRef(0);

  // Get blockchain state
  const { isReady } = useBlockchainState();

  // Load filter preferences from localStorage with defaults (MUST be before hook calls)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_isCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_selectedGame');
    return saved !== null ? saved : 'All';
  });
  const [selectedGames, setSelectedGames] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_selectedGames');
    return saved !== null ? JSON.parse(saved) : null; // null means "use default (all games)"
  });
  const [showOnlyActiveGames, setShowOnlyActiveGames] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_showOnlyActiveGames');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showOnlyMyGames, setShowOnlyMyGames] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_showOnlyMyGames');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [sortPriorities, setSortPriorities] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_sortPriorities');
    return saved !== null ? JSON.parse(saved) : ['waitingForMe'];
  });
  const [filterGameID, setFilterGameID] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_filterGameID');
    return saved !== null ? saved : '';
  });
  const [filterOfferID, setFilterOfferID] = useState(() => {
    const saved = localStorage.getItem('gameInstancesTable_filterOfferID');
    return saved !== null ? saved : '';
  });

  // Get game instances data using new hook (only when not using pre-filtered data)
  const shouldFetchGameInstances = !preFilteredGameInstances;
  const {
    data: gameInstances,
    loading,
    error,
    refresh: refreshGameInstances
  } = useGameInstances({
    activeOnly: showOnlyActiveGames,
    gameFilter: selectedGame === 'All' ? '' : selectedGame
  }, shouldFetchGameInstances);

  // Use pre-filtered instances if provided, otherwise use context data
  const queriedGameInstances = preFilteredGameInstances || gameInstances || [];

  // Use draw offers hook to get real-time draw offers data
  const { drawOffers } = useDrawOffers(playerAccount, queriedGameInstances);

  // DICY-style initialization effect
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      preFilteredRef.current = preFilteredGameInstances;
    }
  }, [isInitialized, preFilteredGameInstances]);

  // Use centralized data - fetch game instances when config is ready or filters change
  // Skip fetching if pre-filtered data is provided (PersonalizedSection use case)
  useEffect(() => {
    if (!isInitialized) {
      return; // Wait for initialization
    }

    if (preFilteredGameInstances) {
      // Throttle logging to reduce spam (log every 500th occurrence)
      logCounterRef.current++;
      if (logCounterRef.current % 500 === 1) {
        console.log(`📦 GameInstancesTable: Using pre-filtered data, skipping centralized fetch (${logCounterRef.current} times)`);
      }
      return;
    }

    if (!isReady) {
      console.log('⏳ GameInstancesTable: Blockchain not ready, skipping fetch');
      return;
    }

    console.log('🔄 GameInstancesTable: Data will be fetched automatically by useGameInstances hook');
    // Note: Data fetching is now handled automatically by the useGameInstances hook
  }, [isInitialized, preFilteredGameInstances, isReady, selectedGame, showOnlyMyGames, showOnlyActiveGames, playerAccount]);
  
  // Save filter preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('gameInstancesTable_isCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_selectedGame', selectedGame);
  }, [selectedGame]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_selectedGames', JSON.stringify(selectedGames));
  }, [selectedGames]);

  // Set default to all games if no localStorage value exists and games are available
  useEffect(() => {
    if (selectedGames === null && games && games.length > 0) {
      setSelectedGames(games.map(g => g.name));
    }
  }, [games, selectedGames]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_showOnlyActiveGames', JSON.stringify(showOnlyActiveGames));
  }, [showOnlyActiveGames]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_showOnlyMyGames', JSON.stringify(showOnlyMyGames));
  }, [showOnlyMyGames]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_sortPriorities', JSON.stringify(sortPriorities));
  }, [sortPriorities]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_filterGameID', filterGameID);
  }, [filterGameID]);

  useEffect(() => {
    localStorage.setItem('gameInstancesTable_filterOfferID', filterOfferID);
  }, [filterOfferID]);
  





  // Filter game instances based on selectedGames and ID filters
  const filteredGameInstances = queriedGameInstances.filter((gameInstance) => {
    // Handle multi-game selection
    let isSelectedInMultiGame = true;
    if (selectedGames === null) {
      isSelectedInMultiGame = true; // null means "all games" (default)
    } else if (selectedGames.length === 0) {
      isSelectedInMultiGame = false; // Empty array = show no games
    } else {
      isSelectedInMultiGame = selectedGames.includes(gameInstance.gameName);
    }
    
    // Handle game ID filtering
    const matchesGameID = !filterGameID || filterGameID.trim() === '' || String(gameInstance.gameID) === filterGameID.trim();
    
    // Handle offer ID filtering
    const matchesOfferID = !filterOfferID || filterOfferID.trim() === '' || String(gameInstance.offerID) === filterOfferID.trim();
    
    return isSelectedInMultiGame && matchesGameID && matchesOfferID;
  });

  const enrichedGameInstances = filteredGameInstances.map((gameInstance) => {
    // Helper function to normalize status values (consistent with PersonalizedSection)
    const normalizeStatus = (status) => {
      if (status === undefined || status === null) return ethersUtils.toBN(-1);
      if (typeof status === 'string') return ethersUtils.toBN(status);
      if (typeof status === 'number') return ethersUtils.toBN(status);
      return status; // Already BigNumber
    };

    // Consistent account comparison (case-insensitive like PersonalizedSection)
    const accountLower = (playerAccount || '').toLowerCase();
    const isPlayer1 = gameInstance.player1 && String(gameInstance.player1).toLowerCase() === accountLower;
    const isPlayer2 = gameInstance.player2 && String(gameInstance.player2).toLowerCase() === accountLower;
    const UserInvolved = isPlayer1 || isPlayer2;

    // Consistent status handling
    const status = normalizeStatus(gameInstance.status);
    const gameActive = (status.eq(ethersUtils.toBN(0)) || status.eq(ethersUtils.toBN(1)) || status.eq(ethersUtils.toBN(6)));

    // Consistent turn logic (matches PersonalizedSection exactly)
    const isMyTurn = (isPlayer1 && (status.eq(ethersUtils.toBN(0)) || status.eq(ethersUtils.toBN(6)))) ||
                     (isPlayer2 && (status.eq(ethersUtils.toBN(1)) || status.eq(ethersUtils.toBN(6))));
    const UserTurn = isMyTurn;

    // Calculate time remaining with robust time sync handling
    const lastMoveTime = new Date(Number(gameInstance.lastMoveTime) * 1000);
    const timeElapsedSinceLastMove = Math.floor((Date.now() - lastMoveTime.getTime()) / (1000 * 60));
    const timeoutMinutes = Number(gameInstance.timeoutSinceLastMove) / 60;
    
    // Handle time sync issues - if elapsed time is negative, assume timeout expired
    const timeElapsedCorrected = Math.max(0, timeElapsedSinceLastMove);
    const timeRemaining = Math.max(0, timeoutMinutes - timeElapsedCorrected);

    // Calculate bet size
    const totalBet = Number(gameInstance.player1Bet) + Number(gameInstance.player2Bet);

    let sortprio;
    let backgroundColor;
    if (UserInvolved) {
      if (gameActive) {
        if (UserTurn) {
          sortprio = 1;
          backgroundColor = '#fef3c7'; // Moderate light orange (requiring attention - user's turn)
        } else {
          sortprio = 2;
          backgroundColor = '#ecfdf5'; // Moderate light green (affecting - opponent's turn)
        }
      } else {
        sortprio = 3;
        backgroundColor = '#ecfdf5'; // Moderate light green (affecting - user participated)
      }
    } else {
      if (gameActive) {
        sortprio = 4;
        backgroundColor = '#ffffff'; // White (general - user not involved)
      } else {
        sortprio = 5;
        backgroundColor = '#f9fafb'; // Light gray (general - inactive, not involved)
      }
    }
    return { ...gameInstance, sortprio, backgroundColor, timeRemaining, totalBet, isWaitingForMe: UserTurn };
  });

  // Sort based on priority list
  const sortedGameInstances = [...enrichedGameInstances].sort((a, b) => {
    for (const sortBy of sortPriorities) {
      let comparison = 0;
      
      switch (sortBy) {
        case 'timeRemaining':
          comparison = a.timeRemaining - b.timeRemaining; // Ascending (least time first)
          break;
        case 'betSize':
          comparison = b.totalBet - a.totalBet; // Descending (highest bet first)
          break;
        case 'waitingForMe':
        default:
          // First by waiting for me, then by original priority
          if (a.isWaitingForMe && !b.isWaitingForMe) comparison = -1;
          else if (!a.isWaitingForMe && b.isWaitingForMe) comparison = 1;
          else comparison = a.sortprio - b.sortprio;
          break;
      }
      
      if (comparison !== 0) {
        return comparison;
      }
    }
    return 0;
  });

  const gamesWaitingForYou = sortedGameInstances.filter(game => game.sortprio === 1);
  const gamesYouPlay = sortedGameInstances.filter(game => game.sortprio === 2);

  const totalBetsX = gamesWaitingForYou.reduce((sum, game) => sum + parseFloat(ethersUtils.fromWei(ethersUtils.toBN(game.player1Bet).add(ethersUtils.toBN(game.player2Bet)).toString(), 'ether')), 0);
  const totalBetsY = gamesYouPlay.reduce((sum, game) => sum + parseFloat(ethersUtils.fromWei(ethersUtils.toBN(game.player1Bet).add(ethersUtils.toBN(game.player2Bet)).toString(), 'ether')), 0);

  // Show loading or error state
  if (error) {
    return (
      <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Connection Error</h3>
        <p className="text-yellow-700">{error}</p>
      </div>
    );
  }

  if (!isReady || loading) {
    return (
      <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-600 mb-2">Loading Game Instances...</h3>
        <p className="text-gray-500">{!isReady ? 'Initializing blockchain connection...' : 'Fetching latest game instances...'}</p>
      </div>
    );
  }

  const lowestSortPrio = sortedGameInstances.length > 0 ? Math.min(...sortedGameInstances.map(game => game.sortprio)) : 5;
  const boxColor = sortedGameInstances.find(game => game.sortprio === lowestSortPrio)?.backgroundColor || '#DCDCDC';

  // Main content component
  const renderContent = () => (
    <div className="space-y-4">
      <GameInstancesFilterPanel
        games={games}
        selectedGame={selectedGame}
        setSelectedGame={setSelectedGame}
        selectedGames={selectedGames}
        setSelectedGames={setSelectedGames}
        showOnlyActiveGames={showOnlyActiveGames}
        setShowOnlyActiveGames={setShowOnlyActiveGames}
        showOnlyMyGames={showOnlyMyGames}
        setShowOnlyMyGames={setShowOnlyMyGames}
        filterGameID={filterGameID}
        setFilterGameID={setFilterGameID}
        filterOfferID={filterOfferID}
        setFilterOfferID={setFilterOfferID}
        sortPriorities={sortPriorities}
        setSortPriorities={setSortPriorities}
        isOpen={isFilterPanelOpen}
        togglePanel={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
      />
      <div className="space-y-4">
        {sortedGameInstances.map((gameInstance) => (
          <GameInstanceCard
            key={gameInstance.offerID.toString()}
            gameInstance={gameInstance}
            killGame={killGame}
            offerDraw={offerDraw}
            acceptDrawOffer={acceptDrawOffer}
            withdrawDrawOffer={withdrawDrawOffer}
            drawOffers={drawOffers[gameInstance.offerID.toString()] || { player1Offer: null, player2Offer: null, player1Stale: false, player2Stale: false }}
            playerAccount={playerAccount}
            gameUrls={gameUrls}
            backgroundColor={gameInstance.backgroundColor}
            ethersUtils={ethersUtils}
            games={games}
          />
        ))}
      </div>
    </div>
  );

  // Return with or without wrapper based on hideWrapper prop
  if (hideWrapper) {
    return (
      <div ref={tableRef}>
        {renderContent()}
      </div>
    );
  }

  return (
    <div ref={tableRef}>
      <div className="flex-grow space-y-4">
        <div 
          className={`collapsible-box ${isCollapsed ? 'collapsed' : 'expanded'}`} 
          style={{ backgroundColor: boxColor }}
        >
          <div 
            className="collapsible-header"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <div>
              {`${gamesWaitingForYou.length} games waiting for you (bets: ${totalBetsX})`}
              <br />
              {`${gamesYouPlay.length} games you play (bets: ${totalBetsY})`}
            </div>
            <div className="text-center mt-2 text-gray-500 text-sm">
              {isCollapsed ? '▼ Click to expand' : '▲ Click to collapse'}
            </div>
          </div>
          {!isCollapsed && (
            <div className="collapsible-content">
              {renderContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  const propsToCheck = [
    'playerAccount', 'hideWrapper', 'games', 'gameUrls',
    'preFilteredGameInstances', 'ethersUtils'
  ];

  for (const prop of propsToCheck) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }

  // Check function references for stability
  const functionsToCheck = ['killGame', 'offerDraw', 'acceptDrawOffer', 'withdrawDrawOffer'];
  for (const func of functionsToCheck) {
    if (prevProps[func] !== nextProps[func]) {
      return false;
    }
  }

  return true; // Props are equal - prevent re-render
};

// Wrap component in React.memo
export default React.memo(GameInstancesTable, arePropsEqual);